import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
  Row,
  Table,
} from "reactstrap";
import axios from "axios";
import { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPhysicalPrizeReview from "./TinyPhysicalPrizeReview";
const API_BASE = "/api/tiny";
const RUNNING = ["pending", "running"];
const PAGE_SIZE = 15;

const COLORS = {
  cardBg: "#1e2530",
  headerBg: "#252d3a",
  inputBg: "#1a1f2b",
  border: "rgba(255,255,255,0.1)",
  borderInput: "rgba(255,255,255,0.2)",
  textPrimary: "#fff",
  textMuted: "rgba(255,255,255,0.62)",
  accent: "#667eea",
  accentGradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  infoGradient: "linear-gradient(135deg, #1f8ef1 0%, #0056b3 100%)",
  warnGradient: "linear-gradient(135deg, #fb6340 0%, #fbb140 100%)",
};

const STATUS_OPTIONS = [
  { label: "全部狀態", value: "" },
  { label: "已上傳", value: "uploaded" },
  { label: "已匯入", value: "imported" },
  { label: "完成", value: "completed" },
  { label: "失敗", value: "failed" },
  { label: "執行中", value: "running" },
  { label: "等待中", value: "pending" },
];

const PARITY_OPTIONS = [
  { label: "全部週別", value: "" },
  { label: "單數周", value: "odd" },
  { label: "雙數周", value: "even" },
];

const STEP_ORDERS = {
  weekly_report: [
    "step0",
    "step1",
    "step1_5",
    "biweekly_prewarm",
    "step2",
    "biweekly_finalize",
    "step2_recalc",
    "step2_5",
    "step3",
    "verify_settlements",
    "step4",
    "step5",
    "pack",
    "pack_biweekly",
  ],
  weekly_import_phase: ["step0", "step1"],
  weekly_phase15: ["step1_5"],
  weekly_generate_phase: [
    "biweekly_prewarm",
    "step2",
    "biweekly_finalize",
    "step2_recalc",
    "step2_5",
    "step3",
    "verify_settlements",
    "step4",
    "step5",
    "pack",
    "pack_biweekly",
  ],
  biweekly_bonus: ["calculate", "generate"],
};

const OPENING_WEEK_START_UTC = Date.UTC(2025, 11, 13); // 2025-12-13, Week1

const parseWeekRangeUTC = (week) => {
  if (!/^\d{8}-\d{8}$/.test(week || "")) return null;
  const sy = Number(week.slice(0, 4));
  const sm = Number(week.slice(4, 6));
  const sd = Number(week.slice(6, 8));
  const ey = Number(week.slice(9, 13));
  const em = Number(week.slice(13, 15));
  const ed = Number(week.slice(15, 17));
  const start = new Date(Date.UTC(sy, sm - 1, sd));
  const end = new Date(Date.UTC(ey, em - 1, ed));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (
    start.getUTCFullYear() !== sy ||
    start.getUTCMonth() + 1 !== sm ||
    start.getUTCDate() !== sd ||
    end.getUTCFullYear() !== ey ||
    end.getUTCMonth() + 1 !== em ||
    end.getUTCDate() !== ed
  ) {
    return null;
  }
  return { start, end };
};

const isMondayToSundayWeek = (week) => {
  const parsed = parseWeekRangeUTC(week);
  if (!parsed) return false;
  const { start, end } = parsed;
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);
  return start.getUTCDay() === 1 && end.getUTCDay() === 0 && diffDays === 6;
};

const getParity = (week) => {
  const parsed = parseWeekRangeUTC(week);
  if (!parsed) return "-";
  const deltaDays = Math.floor((parsed.start.getTime() - OPENING_WEEK_START_UTC) / 86400000);
  if (deltaDays < 0) return "-";
  const weekNo = Math.floor(deltaDays / 7) + 1;
  return weekNo % 2 === 0 ? "雙數周" : "單數周";
};

const statusBadge = (status) => {
  const map = {
    uploaded: { t: "已上傳", bg: "rgba(17,205,239,0.2)", color: "#11cdef" },
    imported: { t: "已匯入", bg: "rgba(251,99,64,0.2)", color: "#fb6340" },
    completed: { t: "完成", bg: "rgba(45,206,137,0.2)", color: "#2dce89" },
    failed: { t: "失敗", bg: "rgba(245,54,92,0.2)", color: "#f5365c" },
    running: { t: "執行中", bg: "rgba(31,142,241,0.2)", color: "#1f8ef1" },
    pending: { t: "等待中", bg: "rgba(149,165,166,0.22)", color: "#c5ccd8" },
    skipped: { t: "略過", bg: "rgba(149,165,166,0.22)", color: "#c5ccd8" },
  };
  const r = map[status] || { t: status || "-", bg: "rgba(149,165,166,0.22)", color: "#c5ccd8" };
  return (
    <Badge
      style={{
        background: r.bg,
        border: `1px solid ${r.color}66`,
        color: r.color,
        fontWeight: 600,
        padding: "4px 10px",
        fontSize: "11px",
      }}
    >
      {r.t}
    </Badge>
  );
};

const prettyBadge = (label, bg, color) => (
  <Badge
    style={{
      background: bg,
      border: `1px solid ${color}66`,
      color,
      fontWeight: 600,
      padding: "4px 10px",
      fontSize: "11px",
    }}
  >
    {label}
  </Badge>
);

const buttonStyle = (gradient) => ({
  background: gradient,
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
});

const fmtMoney = (n) => (n == null ? "-" : "$" + Number(n).toLocaleString());

/**
 * Phase 1.5 對賬狀態 panel（B 方案）
 * 在「執行週期」按鈕下方常駐顯示該週對賬狀態，避免使用者切頁去票券對賬頁查
 */
const Phase15AuditPanel = ({ audit, loading, week, onReload }) => {
  // 狀態判定
  const status = audit?.status;
  const isOk = status === "ok";
  const isBlocked = status === "blocked";
  const notRun = !audit;

  const statusColor = isOk ? "#00d6b4" : isBlocked ? "#fb6340" : "#9A9A9A";
  const statusBg = isOk
    ? "rgba(0, 214, 180, 0.06)"
    : isBlocked
    ? "rgba(251, 99, 64, 0.06)"
    : "rgba(154, 154, 154, 0.06)";

  return (
    <Card style={{ background: statusBg, border: `1px solid ${statusColor}`, marginBottom: 12 }}>
      <CardBody style={{ padding: "12px 16px" }}>
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1 }}>
            <div style={{ color: statusColor, fontWeight: 700, marginBottom: 6 }}>
              <i className={`fa fa-${isOk ? "check-circle" : isBlocked ? "exclamation-triangle" : "info-circle"} mr-2`} />
              {loading
                ? <TinyBusy text="載入該週對賬狀態" style={{ color: "inherit", fontSize: "inherit" }} />
                : isOk
                ? "票券對帳已完成 — 玩家輸贏已實際更新"
                : isBlocked
                ? `票券對帳卡住（${audit.alerts?.length || 0} 個 critical alert）— 產報表已鎖住`
                : "票券對帳尚未執行"}
            </div>

            {isOk && (
              <>
                <div style={{ color: "#fff", fontSize: 14, marginBottom: 4 }}>
                  ✓ 補回玩家輸贏 <strong style={{ color: statusColor, fontSize: 16 }}>{fmtMoney(audit.total_consumed_value)}</strong>
                  {"　"}
                  配對 <strong>{audit.total_consumed_grants || 0}</strong> 張贈送票{"　"}
                  受惠 <strong>{audit.player_compensations?.length || 0}</strong> 玩家
                </div>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
                  📊 已寫入 collection：<code style={{ color: "#5dd8ff" }}>members_weekly.player_p_l</code>{" + "}
                  <code style={{ color: "#5dd8ff" }}>clubs_weekly.p_l</code>{" + "}
                  <code style={{ color: "#5dd8ff" }}>mtt_ticket_grants.consumed_at</code>
                </div>
                {audit.verification && (
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>
                    🔍 抽樣驗證：
                    <strong style={{ color: audit.verification.failures?.length === 0 ? statusColor : "#fb6340" }}>
                      {audit.verification.verified_ok}/{audit.verification.sample_size}
                    </strong> sample re-read DB 對得上
                    （total targets {audit.verification.total_targets}）
                  </div>
                )}
              </>
            )}

            {isBlocked && (
              <div style={{ color: "#fff", fontSize: 14 }}>
                請到票券對賬頁處理（確認是我方發的 / 俱樂部自發）後重跑票券對帳；解除後產報表會自動解鎖。
              </div>
            )}

            {notRun && !loading && (
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                該週尚未執行票券對帳，玩家輸贏目前是 ClubGG raw 數值（含贈送票被誤計的負分）。
              </div>
            )}
          </div>

          <div className="text-right" style={{ marginLeft: 12 }}>
            <div style={{ marginBottom: 6 }}>
              <a
                href="/admin/tiny-tickets"
                style={{ color: "#5dd8ff", fontSize: 12, textDecoration: "none" }}
              >
                <i className="fa fa-external-link-alt mr-1" />
                票券對賬頁
              </a>
            </div>
            <Button
              size="sm"
              outline
              color="secondary"
              onClick={onReload}
              disabled={loading}
              style={{ fontSize: 11, padding: "2px 8px" }}
            >
              <i className="fa fa-refresh" /> 重新載入
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

function TinyReports() {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("weekly");
  const [loadingText, setLoadingText] = useState("載入中...");
  const [loadingCount, setLoadingCount] = useState(0);

  const [uploadWeek, setUploadWeek] = useState("");
  const [runWeek, setRunWeek] = useState("");
  const [uploadedWeeks, setUploadedWeeks] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [renamedFiles, setRenamedFiles] = useState([]);
  const fileRef = useRef(null);

  const [currentJob, setCurrentJob] = useState(null);
  const [startingPhase, setStartingPhase] = useState("");
  const pollRef = useRef(null);
  const [diagnosingJob, setDiagnosingJob] = useState(false);
  const [jobDiagnosis, setJobDiagnosis] = useState(null);
  const [markingStuck, setMarkingStuck] = useState(false);
  const [cancellingJob, setCancellingJob] = useState(false);
  const [retryingPhase, setRetryingPhase] = useState(false);
  const [pollErrorCount, setPollErrorCount] = useState(0);

  // Phase 1.5 票券對賬 audit 狀態（B 方案 panel + C 方案 button badge）
  const [phase15Audit, setPhase15Audit] = useState(null);
  // v2.50.0 實體獎勵確認的閘門（沒完成不能產報表）
  const [prizeGate, setPrizeGate] = useState(null);
  const [prizeOpen, setPrizeOpen] = useState(false);
  const [phase15AuditLoading, setPhase15AuditLoading] = useState(false);

  const [weeklyRows, setWeeklyRows] = useState([]);
  const [weeklyPage, setWeeklyPage] = useState(1);
  const [weeklyTotalPages, setWeeklyTotalPages] = useState(1);
  const [weeklyTotal, setWeeklyTotal] = useState(0);
  const [weeklyPageSize, setWeeklyPageSize] = useState(PAGE_SIZE);
  const [weeklyFilterWeek, setWeeklyFilterWeek] = useState("");
  const [weeklyFilterStatus, setWeeklyFilterStatus] = useState("");
  const [weeklyFilterParity, setWeeklyFilterParity] = useState("");
  const [weeklyQueryWeek, setWeeklyQueryWeek] = useState("");
  const [weeklyQueryStatus, setWeeklyQueryStatus] = useState("");
  const [weeklyQueryParity, setWeeklyQueryParity] = useState("");

  const [biweeklyRows, setBiweeklyRows] = useState([]);
  const [biweeklyPage, setBiweeklyPage] = useState(1);
  const [biweeklyTotalPages, setBiweeklyTotalPages] = useState(1);
  const [biweeklyTotal, setBiweeklyTotal] = useState(0);
  const [biweeklyPageSize, setBiweeklyPageSize] = useState(PAGE_SIZE);
  const [biweeklyFilterId, setBiweeklyFilterId] = useState("");
  const [biweeklyFilterStatus, setBiweeklyFilterStatus] = useState("");
  const [biweeklyQueryId, setBiweeklyQueryId] = useState("");
  const [biweeklyQueryStatus, setBiweeklyQueryStatus] = useState("");
  const [downloadingKey, setDownloadingKey] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingPhase, setPendingPhase] = useState("");
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [uploadWarnings, setUploadWarnings] = useState([]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const beginLoading = useCallback((text = "載入中...") => {
    setLoadingText(text);
    setLoadingCount((c) => c + 1);
  }, []);

  const endLoading = useCallback(() => {
    setLoadingCount((c) => Math.max(0, c - 1));
  }, []);

  const loadUploadWeeks = useCallback(async () => {
    beginLoading("載入可執行週期...");
    try {
      const res = await axios.get(`${API_BASE}/upload/weeks`);
      const weeks = (res.data?.data?.weeks || []).map((x) => x.week).filter(Boolean);
      setUploadedWeeks(weeks);
      if (!runWeek && weeks.length > 0) {
        setRunWeek(weeks[0]);
      }
      return weeks;
    } finally {
      endLoading();
    }
  }, [runWeek, beginLoading, endLoading]);

  const loadLatestJobForWeek = useCallback(
    async (week, { silent = false } = {}) => {
      if (!week) {
        setCurrentJob(null);
        setJobDiagnosis(null);
        stopPolling();
        return null;
      }
      if (!silent) beginLoading("同步任務狀態...");
      try {
        const res = await axios.get(`${API_BASE}/reports/jobs/latest/${week}`);
        const job = res.data?.data || null;
        setCurrentJob(job);
        setPollErrorCount(0);
        if (!job || job.job_id !== jobDiagnosis?.job_id) {
          setJobDiagnosis(null);
        }
        if (job && RUNNING.includes(job.status)) {
          pollJob(job.job_id);
        } else {
          stopPolling();
        }
        return job;
      } catch (e) {
        setError(e.response?.data?.msg || e.message);
        return null;
      } finally {
        if (!silent) endLoading();
      }
    },
    [beginLoading, endLoading, jobDiagnosis?.job_id]
  );

  const loadWeeklyStatus = useCallback(async () => {
    beginLoading("載入每週報表狀態...");
    try {
      const res = await axios.get(`${API_BASE}/reports/status/weekly`, {
        params: {
          page: weeklyPage,
          page_size: PAGE_SIZE,
          week: weeklyQueryWeek || undefined,
          status: weeklyQueryStatus || undefined,
          parity: weeklyQueryParity || undefined,
        },
      });
      const data = res.data?.data || {};
      setWeeklyRows(data.items || []);
      setWeeklyTotalPages(data.total_pages || 1);
      setWeeklyTotal(data.total || 0);
      setWeeklyPageSize(data.page_size || PAGE_SIZE);
    } finally {
      endLoading();
    }
  }, [weeklyPage, weeklyQueryWeek, weeklyQueryStatus, weeklyQueryParity, beginLoading, endLoading]);

  const loadBiweeklyStatus = useCallback(async () => {
    beginLoading("載入雙週獎池報表狀態...");
    try {
      const res = await axios.get(`${API_BASE}/biweekly/status`, {
        params: {
          page: biweeklyPage,
          page_size: PAGE_SIZE,
          biweek_id: biweeklyQueryId || undefined,
          status: biweeklyQueryStatus || undefined,
        },
      });
      const data = res.data?.data || {};
      setBiweeklyRows(data.items || []);
      setBiweeklyTotalPages(data.total_pages || 1);
      setBiweeklyTotal(data.total || 0);
      setBiweeklyPageSize(data.page_size || PAGE_SIZE);
    } finally {
      endLoading();
    }
  }, [biweeklyPage, biweeklyQueryId, biweeklyQueryStatus, beginLoading, endLoading]);

  const loadInit = useCallback(async () => {
    beginLoading("載入報表整合中心...");
    try {
      const [_, __, weeks] = await Promise.all([loadWeeklyStatus(), loadBiweeklyStatus(), loadUploadWeeks()]);
      const initWeek = runWeek || (weeks && weeks.length > 0 ? weeks[0] : "");
      if (initWeek) {
        await loadLatestJobForWeek(initWeek, { silent: true });
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      endLoading();
    }
  }, [loadWeeklyStatus, loadBiweeklyStatus, loadUploadWeeks, loadLatestJobForWeek, runWeek, beginLoading, endLoading]);

  useEffect(() => {
    loadInit();
    return () => stopPolling();
  }, [loadInit]);

  useEffect(() => {
    loadWeeklyStatus().catch((e) => setError(e.response?.data?.msg || e.message));
  }, [loadWeeklyStatus]);

  useEffect(() => {
    loadBiweeklyStatus().catch((e) => setError(e.response?.data?.msg || e.message));
  }, [loadBiweeklyStatus]);

  useEffect(() => {
    if (!runWeek) {
      setCurrentJob(null);
      stopPolling();
      return;
    }
    loadLatestJobForWeek(runWeek, { silent: true });
  }, [runWeek, loadLatestJobForWeek]);

  // Phase 1.5 audit 載入（B+C 方案）— 切週時自動 fetch；job 結束時也 reload
  const loadPhase15Audit = useCallback(async (week) => {
    if (!week) {
      setPhase15Audit(null);
      return;
    }
    setPhase15AuditLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/tickets/week/${week}`);
      setPhase15Audit(res.data?.data?.audit || null);
    } catch (e) {
      // 不打擾用戶 — 是輔助資訊
      setPhase15Audit(null);
    } finally {
      setPhase15AuditLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhase15Audit(runWeek);
  }, [runWeek, loadPhase15Audit]);

  // job 結束（completed/failed）後 reload audit — 確保 Phase 1.5 跑完後 UI 同步
  useEffect(() => {
    if (currentJob && !RUNNING.includes(currentJob.status) && runWeek) {
      loadPhase15Audit(runWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJob?.status]);

  const triggerDownload = async (url, fallbackName, key) => {
    try {
      beginLoading("準備下載檔案...");
      setDownloadingKey(key);
      const res = await axios.get(url, { responseType: "blob" });
      const contentDisposition = res.headers["content-disposition"] || "";
      const matched = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = matched?.[1] || fallbackName;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e.response?.data?.msg || e.message || "下載失敗");
    } finally {
      setDownloadingKey("");
      endLoading();
    }
  };

  const pollJob = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/reports/jobs/${jobId}`);
        const job = res.data?.data;
        if (!job) return;
        setPollErrorCount(0);
        setCurrentJob(job);
        if (!RUNNING.includes(job.status)) {
          stopPolling();
          if (job.status === "completed") {
            setSuccess("流程已完成，狀態列表已更新。");
            await Promise.all([loadWeeklyStatus(), loadBiweeklyStatus()]);
          } else {
            setError(job.error || "流程失敗");
          }
        }
      } catch (_) {
        setPollErrorCount((c) => {
          const next = c + 1;
          if (next >= 3) {
            setError("任務狀態輪詢連續失敗，請點「檢查卡住原因」查看 worker 與 log。");
          }
          return next;
        });
      }
    }, 2500);
  };

  const diagnoseCurrentJob = async () => {
    if (!currentJob?.job_id) return;
    setDiagnosingJob(true);
    setError(null);
    beginLoading("診斷任務狀態中...");
    try {
      const res = await axios.get(`${API_BASE}/reports/jobs/${currentJob.job_id}/diagnose`);
      if (res.data?.code !== 1) {
        throw new Error(res.data?.msg || "診斷失敗");
      }
      const data = res.data?.data || null;
      setJobDiagnosis(data);
      if (data?.looks_stuck) {
        setError("系統判定此任務疑似卡住：worker 已不存在，但狀態仍在 pending/running。");
      } else {
        setSuccess("診斷完成：目前任務狀態正常或可再觀察。");
      }
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setDiagnosingJob(false);
      endLoading();
    }
  };

  const markCurrentJobStuckFailed = async () => {
    if (!currentJob?.job_id) return;
    setMarkingStuck(true);
    setError(null);
    beginLoading("安全標記卡住任務...");
    try {
      const res = await axios.post(`${API_BASE}/reports/jobs/${currentJob.job_id}/mark-stuck-failed`);
      if (res.data?.code !== 1) {
        throw new Error(res.data?.msg || "標記失敗");
      }
      setSuccess(res.data?.data?.message || "已完成卡住修復");
      await Promise.all([loadLatestJobForWeek(runWeek, { silent: true }), loadWeeklyStatus()]);
      await diagnoseCurrentJob();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setMarkingStuck(false);
      endLoading();
    }
  };

  const cancelCurrentJob = async () => {
    if (!currentJob?.job_id) return;
    setCancellingJob(true);
    setError(null);
    beginLoading("取消任務中...");
    try {
      const res = await axios.post(`${API_BASE}/reports/jobs/${currentJob.job_id}/cancel`);
      if (res.data?.code !== 1) {
        throw new Error(res.data?.msg || "取消任務失敗");
      }
      stopPolling();
      setSuccess(res.data?.data?.message || "任務已取消");
      await Promise.all([loadLatestJobForWeek(runWeek, { silent: true }), loadWeeklyStatus()]);
      await diagnoseCurrentJob();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setCancellingJob(false);
      endLoading();
    }
  };

  const retryCurrentPhase = async () => {
    if (!currentJob?.job_type) return;
    const phaseMap = {
      weekly_import_phase: "phase1",
      weekly_phase15: "phase1_5",
      weekly_generate_phase: "phase2",
    };
    const phase = phaseMap[currentJob.job_type];
    if (!phase) {
      setError("此任務類型無法直接重跑，請手動選擇「匯入」或「產報表」。");
      return;
    }
    setRetryingPhase(true);
    try {
      await startPhase(phase);
    } finally {
      setRetryingPhase(false);
    }
  };

  const doUpload = async (force = false) => {
    setUploading(true);
    setUploadPct(0);
    setError(null);
    setSuccess(null);
    beginLoading("上傳原始報表中...");
    try {
      const fd = new FormData();
      fd.append("week", uploadWeek);
      if (force) fd.append("force", "1");
      uploadFiles.forEach((f, idx) => {
        fd.append(`file_${idx}`, f);
        fd.append(`file_${idx}_type`, "raw_report");
      });
      const res = await axios.post(`${API_BASE}/upload/excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setUploadPct(Math.round((ev.loaded * 100) / ev.total));
        },
      });
      if (res.data?.code === -2) {
        setUploadWarnings(res.data?.data?.warnings || [res.data?.msg || "週期警告，請確認是否繼續"]);
        setUploadConfirmOpen(true);
        return;
      }
      if (res.data?.code !== 1) {
        throw new Error(res.data?.msg || "上傳失敗");
      }
      setRenamedFiles(res.data?.data?.renamed_files || []);
      setRunWeek(uploadWeek);
      setUploadFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setSuccess(`上傳完成：${res.data?.data?.files_processed || 0} 個檔案（已重置該週資料夾）`);
      await Promise.all([loadWeeklyStatus(), loadUploadWeeks()]);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setUploading(false);
      endLoading();
    }
  };

  const handleUpload = async () => {
    if (!uploadWeek) return setError("請先輸入上傳週期");
    if (!/^\d{8}-\d{8}$/.test(uploadWeek)) return setError("週期格式需為 YYYYMMDD-YYYYMMDD");
    if (!isMondayToSundayWeek(uploadWeek)) return setError("週期必須是週一到週日，且區間為 7 天");
    if (!uploadFiles.length) return setError("請先選擇 Excel 檔案");
    setError(null);
    beginLoading("檢查週期規則...");
    try {
      const checkRes = await axios.post(`${API_BASE}/upload/validate-week`, { week: uploadWeek });
      const data = checkRes.data?.data || {};
      if (checkRes.data?.code !== 1 || !data.valid) {
        throw new Error(checkRes.data?.msg || data.msg || "週期驗證失敗");
      }
      const warnings = data.warnings || [];
      if (warnings.length > 0) {
        setUploadWarnings(warnings);
        setUploadConfirmOpen(true);
        return;
      }
      await doUpload(false);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      endLoading();
    }
  };

  const startPhase = async (phase) => {
    if (!runWeek) return setError("請先從下拉選擇已上傳週期");
    setStartingPhase(phase);
    setError(null);
    setSuccess(null);
    const phaseLabel = phase === "phase1" ? "匯入" : phase === "phase1_5" ? "票券對帳" : "產報表";
    beginLoading(`啟動${phaseLabel}...`);
    try {
      const url = `${API_BASE}/reports/generate/${phase}`;
      const res = await axios.post(url, { week: runWeek });
      if (res.data?.code !== 1) {
        // v2.49.0：輸贏檢查不通過時，把前幾筆問題一起顯示（哪位玩家 / 哪家俱樂部對不上）
        const problems = res.data?.data?.pl_problems || [];
        const detail = problems.length ? `：${problems.slice(0, 5).join("；")}` : "";
        throw new Error((res.data?.msg || "任務建立失敗") + detail);
      }
      const jobId = res.data?.data?.job_id;
      setSuccess(`${phaseLabel}已啟動，Job ID: ${jobId}`);
      setCurrentJob({ job_id: jobId, status: "pending", message: "等待啟動..." });
      setJobDiagnosis(null);
      setPollErrorCount(0);
      if (jobId) pollJob(jobId);
      await loadWeeklyStatus();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setStartingPhase("");
      endLoading();
    }
  };

  const requestStartPhase = (phase) => {
    if (!runWeek) {
      setError("請先從下拉選擇已上傳週期");
      return;
    }
    setPendingPhase(phase);
    setConfirmModalOpen(true);
  };

  const handleConfirmStartPhase = async () => {
    const phase = pendingPhase;
    setConfirmModalOpen(false);
    setPendingPhase("");
    if (!phase) return;
    await startPhase(phase);
  };

  const handleConfirmUpload = async () => {
    setUploadConfirmOpen(false);
    await doUpload(true);
  };

  const handleWeeklyQuery = () => {
    setWeeklyPage(1);
    setWeeklyQueryWeek(weeklyFilterWeek.trim());
    setWeeklyQueryStatus(weeklyFilterStatus);
    setWeeklyQueryParity(weeklyFilterParity);
  };

  const handleBiweeklyQuery = () => {
    setBiweeklyPage(1);
    setBiweeklyQueryId(biweeklyFilterId.trim());
    setBiweeklyQueryStatus(biweeklyFilterStatus);
  };

  const isRunning = currentJob && RUNNING.includes(currentJob.status);
  const stepOrder = STEP_ORDERS[currentJob?.job_type] || [];
  const sortedStepEntries = Object.entries(currentJob?.steps || {}).sort(([aId], [bId]) => {
    const ai = stepOrder.indexOf(aId);
    const bi = stepOrder.indexOf(bId);
    const ar = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
    const br = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
    if (ar !== br) return ar - br;
    return aId.localeCompare(bId);
  });

  return (
    <div className="content">
      {/* beginLoading / endLoading 的 ref-count 機制不動，只把呈現從全屏 modal 改成 fixed 遮罩；
          文案尾巴的「...」由元件畫三個點，這裡剝掉 */}
      <TinyLoadingOverlay fixed active={loadingCount > 0} text={loadingText.replace(/[.…]+$/, "")}>
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      <TinyPageHeader
        title="報表整合中心"
        description="上傳原始報表、分段執行流程、查詢狀態並下載週報與雙週獎池報表。"
        tabs={{
          items: [
            {
              key: "run",
              label: "上傳與執行",
              // 執行中換成轉圈 —— 這是「切走之後看不到 job」的補償
              icon: isRunning ? "fa fa-spinner fa-spin" : "fa fa-play-circle",
            },
            { key: "weekly", label: "每週報表狀態", icon: "fa fa-calendar-check" },
            { key: "biweekly", label: "雙週獎池報表狀態", icon: "fa fa-trophy" },
          ],
          active: activeTab,
          onChange: setActiveTab,
        }}
      />

      {/* ⚠️ 這頁原本是「上傳(lg=5) ｜ 執行流程(lg=7)」並排，底下才是報表狀態表。
             問題是執行流程那張卡不只三顆按鈕 —— Phase 1.5 對賬面板、進度條、
             排查結果、步驟表全在裡面，跑起來輕鬆 800px 以上，
             於是**一打開這頁就看不到主 table**（Brad 2026-09-10 回報）。
          → 改成三個頁面層級的 tab：上傳與執行 ／ 每週報表狀態 ／ 雙週獎池報表狀態。
          🔑 **預設落在「每週報表狀態」不是第一個 tab** —— 不然打開頁面
             還是先看到那一長條，等於沒解。
          ⚠️ 代價是「切到狀態表時看不到跑著的 job」—— 所以執行中時
             「上傳與執行」那個 tab 的圖示會變成轉圈（見 header 的 tabs）。
          ⚠️ 上傳與執行改成上下全寬而不是並排：它們本來就是 step 0 → step 1/2
             的順序，全寬之後三顆分段按鈕也才不會在 27 吋螢幕上換行。 */}

      {activeTab === "run" && (
        <>
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <CardTitle tag="h5" style={{ color: COLORS.textPrimary, margin: 0 }}>
            <i className="fa fa-cloud-upload mr-2" style={{ color: "#2dce89" }} />
            上傳原始報表
          </CardTitle>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label style={{ color: "rgba(255,255,255,0.8)" }}>手動輸入週期</Label>
            <Input
              value={uploadWeek}
              onChange={(e) => setUploadWeek(e.target.value.trim())}
              placeholder="20260105-20260111"
              style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
            />
          </FormGroup>
          <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 10 }}>
            每次上傳都會先清空該週資料夾，並改成固定檔名後覆蓋。
          </div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
            style={{ display: "none" }}
          />
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "#1f8ef1";
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
              if (uploading) return;
              const dropped = Array.from(e.dataTransfer.files || []).filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
              if (dropped.length > 0) setUploadFiles(dropped);
            }}
            style={{
              border: "2px dashed rgba(255,255,255,0.15)",
              borderRadius: 8,
              padding: uploadFiles.length > 0 ? "12px 16px" : "20px 16px",
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              transition: "border-color 0.2s, background 0.2s",
              background: "rgba(255,255,255,0.02)",
              opacity: uploading ? 0.5 : 1,
              marginBottom: 10,
            }}
          >
            {uploadFiles.length === 0 ? (
              <>
                <i className="fa fa-cloud-upload" style={{ fontSize: "1.6rem", color: "#1f8ef1", display: "block", marginBottom: 6 }} />
                <span style={{ color: "#9A9A9A", fontSize: "0.88em" }}>
                  點擊選擇或拖曳 <strong style={{ color: "#fff" }}>Excel</strong> 檔案至此
                </span>
              </>
            ) : (
              <div style={{ textAlign: "left", color: "#bfc7d5" }}>
                {uploadFiles.map((f, i) => (
                  <div key={`${f.name}_${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span>{f.name}</span>
                    <span>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {uploading && <Progress value={uploadPct} style={{ height: 8, marginBottom: 10 }} />}
          <Button
            style={buttonStyle(COLORS.infoGradient)}
            disabled={uploading || uploadFiles.length === 0}
            onClick={handleUpload}
          >
            {uploading ? <TinyBusy light style={{ marginRight: 4 }} /> : <i className="fa fa-upload mr-1" />}
            {uploading ? " 上傳中" : " 開始上傳"}
          </Button>
          {renamedFiles.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 120, overflow: "auto", fontSize: 12, color: "#9A9A9A" }}>
              {renamedFiles.map((item) => (
                <div key={`${item.original_name}_${item.saved_name}`}>
                  {item.original_name} → {item.saved_name}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
        <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
          <CardTitle tag="h5" style={{ color: COLORS.textPrimary, margin: 0 }}>
            <i className="fa fa-play-circle mr-2" style={{ color: "#1f8ef1" }} />
            執行流程
          </CardTitle>
        </CardHeader>
        <CardBody>
          <TinyWeekSelector
            label={<span style={{ color: "rgba(255,255,255,0.8)" }}>執行週期</span>}
            options={uploadedWeeks}
            value={runWeek}
            onChange={setRunWeek}
            placeholder={uploadedWeeks.length === 0 ? "尚無可執行週期（請先上傳）" : ""}
            showNav={uploadedWeeks.length > 0}
            inputStyle={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
          />
          {/* ⚠️ 段號一律用阿拉伯數字（第 1 / 1.5 / 2 段）。
                 原本是「第一段」「第 1.5 段」「第二段」中文與數字混用 ——
                 1.5 沒有不彆扭的中文寫法，所以是把另外兩個改成數字。
              ⚠️ 三顆鈕改成 `flex: 1 1 0` 等寬平分而不是各自撐開：
                 原本靠內容決定寬度，第 1.5 段那顆最長、又帶 badge，
                 27 吋螢幕就會擠到換行。標題與副標分兩行後，
                 單顆需要的寬度砍掉將近一半。 */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            {[
              { phase: "phase1", grad: COLORS.accentGradient, icon: "fa-database",
                title: "匯入", sub: "清理 + 匯入 ClubGG 原始檔" },
              { phase: "phase1_5", grad: COLORS.infoGradient, icon: "fa-ticket",
                title: "票券對帳", sub: "用票補回玩家輸贏",
                badge: phase15Audit?.status === "ok"
                  ? <Badge color="success" style={{ marginLeft: 6 }}>✓</Badge>
                  : phase15Audit?.status === "blocked"
                    ? <Badge color="danger" style={{ marginLeft: 6 }}>⚠</Badge>
                    : null },
              { phase: "prize", grad: "linear-gradient(135deg, #2dce89 0%, #2dcecc 100%)",
                icon: "fa-gift", title: "實體獎勵確認", sub: "iPhone 等實體獎勵扣除",
                badge: prizeGate?.ok
                  ? <Badge color="success" style={{ marginLeft: 6 }}>✓</Badge>
                  : prizeGate ? <Badge color="warning" style={{ marginLeft: 6 }}>待完成</Badge> : null },
              { phase: "phase2", grad: COLORS.warnGradient, icon: "fa-calculator",
                title: "產報表",
                sub: prizeGate && !prizeGate.ok ? "請先完成實體獎勵確認" : "計算 + 產 Excel",
                badge: phase15Audit?.status === "blocked" || (prizeGate && !prizeGate.ok)
                  ? <Badge color="danger" style={{ marginLeft: 6 }}>🔒</Badge>
                  : null },
            ].map((b) => (
              <Button
                key={b.phase}
                style={{ ...buttonStyle(b.grad), flex: "1 1 0", minWidth: 130, padding: "8px 10px",
                         ...(b.phase === "phase2" && prizeGate && !prizeGate.ok ? { opacity: 0.45 } : {}) }}
                disabled={b.phase === "prize" ? !runWeek
                  : (isRunning || startingPhase !== "" || !runWeek
                     || (b.phase === "phase2" && prizeGate && !prizeGate.ok))}
                onClick={() => (b.phase === "prize"
                  ? (setPrizeOpen(true), document.getElementById("prize-step")?.scrollIntoView({ behavior: "smooth" }))
                  : requestStartPhase(b.phase))}
              >
                {startingPhase === b.phase ? (
                  <span style={{ fontSize: 13 }}><TinyBusy light style={{ marginRight: 4 }} />啟動中</span>
                ) : (
                  <span style={{ display: "block", lineHeight: 1.25 }}>
                    <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                      <i className={`fa ${b.icon} mr-1`} />{b.title}{b.badge}
                    </span>
                    <span style={{ display: "block", fontSize: 11, fontWeight: 500, opacity: 0.85, whiteSpace: "nowrap" }}>
                      {b.sub}
                    </span>
                  </span>
                )}
              </Button>
            ))}
          </div>

          {/* Phase 1.5 對賬狀態 panel — 切週自動 fetch、job 結束後 reload */}
          {runWeek && (
            <Phase15AuditPanel
              audit={phase15Audit}
              loading={phase15AuditLoading}
              week={runWeek}
              onReload={() => loadPhase15Audit(runWeek)}
            />
          )}

          {/* v2.50.0 實體獎勵確認（票券對帳之後、產報表之前）—— 關鍵字可以事先填；沒完成不能產報表 */}
          {runWeek && (
            <div id="prize-step" style={{
              border: `1px solid ${prizeGate?.ok ? "rgba(45,206,137,0.45)" : "rgba(251,99,64,0.6)"}`,
              background: prizeGate?.ok ? "rgba(45,206,137,0.06)" : "rgba(251,99,64,0.08)",
              borderRadius: 8, padding: "12px 16px", marginBottom: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ fontWeight: 700, color: prizeGate?.ok ? "#2dce89" : "#fb6340" }}>
                  <i className={`fa ${prizeGate?.ok ? "fa-circle-check" : "fa-gift"} mr-2`} />
                  實體獎勵確認：{prizeGate ? prizeGate.reason : "讀取中"}
                </div>
                <Button size="sm" color="link" style={{ color: "#9A9A9A" }} onClick={() => setPrizeOpen(!prizeOpen)}>
                  {prizeOpen || (prizeGate && !prizeGate.ok) ? "收起 ▴" : "展開 ▾"}
                </Button>
              </div>
              <div style={{ display: prizeOpen || (prizeGate && !prizeGate.ok) ? "block" : "none", marginTop: 12 }}>
                <TinyPhysicalPrizeReview week={runWeek} onGate={setPrizeGate} refreshKey={currentJob?.status} compact />
              </div>
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            {runWeek ? (
              getParity(runWeek) === "雙數周"
                ? prettyBadge("雙數周", "rgba(31, 142, 241, 0.15)", "#1f8ef1")
                : prettyBadge("單數周", "rgba(149,165,166,0.18)", "#c5ccd8")
            ) : (
              <Badge color="dark">未選擇週期</Badge>
            )}
          </div>

          {currentJob ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small style={{ color: "#9A9A9A" }}>Job: {currentJob.job_id}</small>
                {statusBadge(currentJob.status)}
              </div>
              {!!currentJob.worker_pid && (
                <div style={{ color: "#9A9A9A", fontSize: 12, marginBottom: 6 }}>
                  Worker PID: {currentJob.worker_pid}
                </div>
              )}
              <Progress value={currentJob.progress || 0} animated={isRunning} />
              <div style={{ color: "#9A9A9A", fontSize: 12, marginTop: 6 }}>{currentJob.message}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Button size="sm" color="secondary" onClick={diagnoseCurrentJob} disabled={diagnosingJob}>
                  {diagnosingJob ? <><TinyBusy light style={{ marginRight: 4 }} />診斷中</> : "檢查卡住原因"}
                </Button>
                {(jobDiagnosis?.safe_actions?.can_mark_stuck_failed || RUNNING.includes(currentJob.status)) && (
                  <Button size="sm" color="danger" onClick={markCurrentJobStuckFailed} disabled={markingStuck}>
                    {markingStuck ? <><TinyBusy light style={{ marginRight: 4 }} />處理中</> : "安全標記卡住（不動資料）"}
                  </Button>
                )}
                {RUNNING.includes(currentJob.status) && (
                  <Button size="sm" color="warning" onClick={cancelCurrentJob} disabled={cancellingJob}>
                    {cancellingJob ? <><TinyBusy light style={{ marginRight: 4 }} />取消中</> : "取消任務"}
                  </Button>
                )}
                {!RUNNING.includes(currentJob.status) &&
                  ["weekly_import_phase", "weekly_generate_phase"].includes(currentJob.job_type) && (
                    <Button size="sm" color="info" onClick={retryCurrentPhase} disabled={retryingPhase || !runWeek}>
                      {retryingPhase ? <><TinyBusy light style={{ marginRight: 4 }} />重跑中</> : "重跑上一階段"}
                    </Button>
                  )}
              </div>
              {jobDiagnosis && (
                <div
                  style={{
                    marginTop: 10,
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: 8,
                    padding: 10,
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  <div style={{ color: "#fff", fontWeight: 600, marginBottom: 6 }}>排查結果</div>
                  <div style={{ color: "#bfc7d5", fontSize: 12, lineHeight: 1.6 }}>
                    <div>判定：{jobDiagnosis.looks_stuck ? "疑似卡住" : "正常/可觀察"}</div>
                    <div>原因：{jobDiagnosis.reason || "-"}</div>
                    <div>Worker PID：{jobDiagnosis.worker_pid || "-"}</div>
                    <div>Worker 存活：{jobDiagnosis.process_running ? "是" : "否"}</div>
                    <div>任務存在秒數：{jobDiagnosis.age_seconds ?? "-"}</div>
                  </div>
                  {!!jobDiagnosis.log_tail && (
                    <pre
                      style={{
                        marginTop: 8,
                        marginBottom: 0,
                        maxHeight: 120,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        color: "#9ec2ff",
                        background: "rgba(5,8,14,0.65)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 6,
                        padding: 8,
                        fontSize: 11,
                      }}
                    >
                      {jobDiagnosis.log_tail}
                    </pre>
                  )}
                </div>
              )}
              <div style={{ maxHeight: 190, overflow: "auto", marginTop: 10 }}>
                <Table size="sm" borderless style={{ color: "rgba(255,255,255,0.85)", marginBottom: 0 }}>
                  <tbody>
                    {sortedStepEntries.map(([sid, step]) => (
                      <tr key={sid}>
                        <td style={{ width: 140, color: "#fff" }}>
                          {stepOrder.indexOf(sid) >= 0 ? `${stepOrder.indexOf(sid) + 1}. ` : ""}
                          {step.name || sid}
                        </td>
                        <td style={{ width: 90 }}>{statusBadge(step.status)}</td>
                        <td style={{ color: "rgba(255,255,255,0.65)" }}>{step.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <div style={{ color: "#9A9A9A" }}>尚未啟動任務</div>
          )}
        </CardBody>
      </Card>
        </>
      )}

      {activeTab !== "run" && (
        <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
          <CardBody>
          {activeTab === "weekly" ? (
            <>
              <Row className="mb-2">
                <Col md="4">
                  <Input
                    value={weeklyFilterWeek}
                    onChange={(e) => setWeeklyFilterWeek(e.target.value.trim())}
                    placeholder="週期關鍵字"
                    style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
                  />
                </Col>
                <Col md="3">
                  <Input
                    type="select"
                    value={weeklyFilterStatus}
                    onChange={(e) => setWeeklyFilterStatus(e.target.value)}
                    style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <Input
                    type="select"
                    value={weeklyFilterParity}
                    onChange={(e) => setWeeklyFilterParity(e.target.value)}
                    style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
                  >
                    {PARITY_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="3">
                  <Button color="secondary" onClick={handleWeeklyQuery}>
                    查詢
                  </Button>
                </Col>
              </Row>
              <div style={{ color: "#9A9A9A", fontSize: 12, marginBottom: 8 }}>
                共 {weeklyTotal} 筆，每頁 {weeklyPageSize} 筆
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table size="sm">
                  <thead>
                    <tr style={{ color: "#9A9A9A" }}>
                      <th>週期</th>
                      <th>週別</th>
                      <th>狀態</th>
                      <th>訊息</th>
                      <th>ZIP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyRows.map((row) => (
                      <tr key={row.week} style={{ color: "#fff" }}>
                        <td>{row.week}</td>
                        <td>
                          {row.parity === "even"
                            ? prettyBadge("雙數周", "rgba(31, 142, 241, 0.15)", "#1f8ef1")
                            : prettyBadge("單數周", "rgba(149,165,166,0.18)", "#c5ccd8")}
                        </td>
                        <td>{statusBadge(row.status)}</td>
                        <td style={{ color: "#bfc7d5" }}>{row.message || "-"}</td>
                        <td>
                          <Button
                            size="sm"
                            style={buttonStyle("linear-gradient(135deg,#2dce89,#26a770)")}
                            disabled={!row.zip_ready || downloadingKey === `weekly-${row.week}`}
                            onClick={() =>
                              triggerDownload(
                                `${API_BASE}/reports/${row.week}/download-all`,
                                `${row.week}_all_reports.zip`,
                                `weekly-${row.week}`
                              )
                            }
                          >
                            {downloadingKey === `weekly-${row.week}`
                              ? <><TinyBusy light style={{ marginRight: 4 }} />下載中</>
                              : <><i className="fa fa-download mr-1" />下載</>}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pagination>
                <PaginationItem disabled={weeklyPage <= 1}>
                  <PaginationLink previous href="#" onClick={(e) => { e.preventDefault(); setWeeklyPage((p) => Math.max(1, p - 1)); }} />
                </PaginationItem>
                <PaginationItem disabled>
                  <PaginationLink href="#">{weeklyPage} / {weeklyTotalPages}</PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={weeklyPage >= weeklyTotalPages}>
                  <PaginationLink next href="#" onClick={(e) => { e.preventDefault(); setWeeklyPage((p) => Math.min(weeklyTotalPages, p + 1)); }} />
                </PaginationItem>
              </Pagination>
            </>
          ) : (
            <>
              <Row className="mb-2">
                <Col md="4">
                  <Input
                    value={biweeklyFilterId}
                    onChange={(e) => setBiweeklyFilterId(e.target.value.trim())}
                    placeholder="雙週 ID 關鍵字"
                    style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
                  />
                </Col>
                <Col md="3">
                  <Input
                    type="select"
                    value={biweeklyFilterStatus}
                    onChange={(e) => setBiweeklyFilterStatus(e.target.value)}
                    style={{ background: COLORS.inputBg, color: "#fff", border: `1px solid ${COLORS.borderInput}` }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="5">
                  <Button color="secondary" onClick={handleBiweeklyQuery}>
                    查詢
                  </Button>
                </Col>
              </Row>
              <div style={{ color: "#9A9A9A", fontSize: 12, marginBottom: 8 }}>
                共 {biweeklyTotal} 筆，每頁 {biweeklyPageSize} 筆
              </div>
              <div style={{ overflowX: "auto" }}>
                <Table size="sm">
                  <thead>
                    <tr style={{ color: "#9A9A9A" }}>
                      <th>雙週 ID</th>
                      <th>狀態</th>
                      <th>訊息</th>
                      <th>ZIP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {biweeklyRows.map((row) => (
                      <tr key={row.biweek_id} style={{ color: "#fff" }}>
                        <td>{row.biweek_id}</td>
                        <td>{statusBadge(row.status)}</td>
                        <td style={{ color: "#bfc7d5" }}>{row.message || "-"}</td>
                        <td>
                          <Button
                            size="sm"
                            style={buttonStyle("linear-gradient(135deg,#11cdef,#1171ef)")}
                            disabled={!row.zip_ready || downloadingKey === `biweekly-${row.biweek_id}`}
                            onClick={() =>
                              triggerDownload(
                                `${API_BASE}/biweekly/download-all/${row.biweek_id}`,
                                `${row.biweek_id}_biweekly_reports.zip`,
                                `biweekly-${row.biweek_id}`
                              )
                            }
                          >
                            {downloadingKey === `biweekly-${row.biweek_id}`
                              ? <><TinyBusy light style={{ marginRight: 4 }} />下載中</>
                              : <><i className="fa fa-download mr-1" />下載</>}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pagination>
                <PaginationItem disabled={biweeklyPage <= 1}>
                  <PaginationLink previous href="#" onClick={(e) => { e.preventDefault(); setBiweeklyPage((p) => Math.max(1, p - 1)); }} />
                </PaginationItem>
                <PaginationItem disabled>
                  <PaginationLink href="#">{biweeklyPage} / {biweeklyTotalPages}</PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={biweeklyPage >= biweeklyTotalPages}>
                  <PaginationLink next href="#" onClick={(e) => { e.preventDefault(); setBiweeklyPage((p) => Math.min(biweeklyTotalPages, p + 1)); }} />
                </PaginationItem>
              </Pagination>
            </>
          )}
          </CardBody>
        </Card>
      )}

      <Modal
        isOpen={confirmModalOpen}
        toggle={() => setConfirmModalOpen(false)}
        style={{ marginTop: "110px" }}
        backdropClassName="tiny-reports-modal-backdrop"
        contentClassName="tiny-reports-modal-content"
      >
        <ModalHeader
          toggle={() => setConfirmModalOpen(false)}
          style={{
            background: "#2b3444",
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: "#fb6340" }} />
          確認執行流程
        </ModalHeader>
        <ModalBody
          style={{
            background: "#263142",
            color: "#fff",
            borderLeft: "1px solid rgba(255,255,255,0.18)",
            borderRight: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <div style={{ marginBottom: 8 }}>
            你即將執行：
            <strong style={{ marginLeft: 6 }}>
              {pendingPhase === "phase1" ? "匯入（清理 + 匯入）" :
               pendingPhase === "phase1_5" ? "票券對帳" :
               "產報表（計算 + 產 Excel）"}
            </strong>
          </div>
          <div>
            週期：<strong>{runWeek || "-"}</strong>
          </div>
          {pendingPhase === "phase1" && (
            <div style={{ marginTop: 10, color: "#bfc7d5", fontSize: 13 }}>
              匯入會清理該週既有資料，並重新匯入上傳檔案；已做過的用票補回與實體獎勵扣除會自動套回。
            </div>
          )}
          {pendingPhase === "phase1_5" && (
            <div style={{ marginTop: 10, color: "#bfc7d5", fontSize: 13 }}>
              票券對帳會比對該週免費賽得獎與用票報名，配對 Trade Record 贈送票，
              並補回玩家 / 俱樂部的 winloss。若有 critical alert，產報表會被擋住，
              請先到票券對賬頁處理後重跑票券對帳。
            </div>
          )}
          {pendingPhase === "phase2" && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: "8px",
                background: "rgba(251,99,64,0.12)",
                border: "1px solid rgba(251,99,64,0.45)",
              }}
            >
              <div style={{ color: "#fb6340", fontWeight: 700, marginBottom: 8 }}>
                在確定要開始計算 + 產報表前，請確認本周下列事情已處理完畢：
              </div>
              <ul style={{ marginBottom: 0, paddingLeft: 20, color: "#ffd7ce", fontSize: 13, lineHeight: 1.7 }}>
                <li>若有送票等行為，請確定工程師已手動調整好玩家 &amp; 俱樂部之輸贏</li>
                <li>請確認俱樂部管理頁面裡的本部俱樂部設置正確，已為最新版本</li>
                <li>ecobot 之俱樂部 &amp; 超代設置正確</li>
                <li>手動登記額外交收已處理完畢（獎勵、罰款等等）</li>
                <li>活動匯入之額外交收已處理完畢（AA守護光環等等）</li>
                <li>所有俱樂部、直營超代、直營代理之分潤規則皆為最新版本</li>
              </ul>
            </div>
          )}
        </ModalBody>
        <ModalFooter
          style={{
            background: "#2b3444",
            borderTop: "1px solid rgba(255,255,255,0.18)",
            borderLeft: "1px solid rgba(255,255,255,0.18)",
            borderRight: "1px solid rgba(255,255,255,0.18)",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            boxShadow: "0 18px 40px rgba(0,0,0,0.5)",
          }}
        >
          <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
            取消
          </Button>
          <Button
            style={buttonStyle(pendingPhase === "phase1" ? COLORS.accentGradient : COLORS.warnGradient)}
            onClick={handleConfirmStartPhase}
          >
            確認執行
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={uploadConfirmOpen}
        toggle={() => setUploadConfirmOpen(false)}
        style={{ marginTop: "110px" }}
        backdropClassName="tiny-reports-modal-backdrop"
        contentClassName="tiny-reports-modal-content"
      >
        <ModalHeader
          toggle={() => setUploadConfirmOpen(false)}
          style={{
            background: "#2b3444",
            color: "#fff",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: "#fb6340" }} />
          週期警告確認
        </ModalHeader>
        <ModalBody
          style={{
            background: "#263142",
            color: "#fff",
            borderLeft: "1px solid rgba(255,255,255,0.18)",
            borderRight: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <div style={{ marginBottom: 8 }}>
            以下警告成立，若繼續會覆蓋該週上傳資料並更新/新增狀態文件：
          </div>
          <ul style={{ marginBottom: 0, paddingLeft: 18, color: "#bfc7d5" }}>
            {uploadWarnings.map((w, idx) => (
              <li key={`${w}-${idx}`} style={{ marginBottom: 6 }}>
                {w}
              </li>
            ))}
          </ul>
        </ModalBody>
        <ModalFooter
          style={{
            background: "#2b3444",
            borderTop: "1px solid rgba(255,255,255,0.18)",
            borderLeft: "1px solid rgba(255,255,255,0.18)",
            borderRight: "1px solid rgba(255,255,255,0.18)",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            boxShadow: "0 18px 40px rgba(0,0,0,0.5)",
          }}
        >
          <Button color="secondary" onClick={() => setUploadConfirmOpen(false)}>
            取消
          </Button>
          <Button style={buttonStyle(COLORS.warnGradient)} onClick={handleConfirmUpload}>
            仍要上傳
          </Button>
        </ModalFooter>
      </Modal>
      <style>{`
        .tiny-reports-modal-backdrop.show {
          opacity: 0.75 !important;
          background-color: rgba(5, 10, 18, 0.95) !important;
        }
        .tiny-reports-modal-content {
          border: 1px solid rgba(255,255,255,0.2) !important;
          border-radius: 12px !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.55) !important;
          background: transparent !important;
        }
      `}</style>
      </TinyLoadingOverlay>
    </div>
  );
}

export default TinyReports;

